import React, { useMemo, useCallback } from "react";
import { useStepContext } from "../Wizard/WizardStepContextProvider";
import { useStepResult } from "../Wizard/WizardResultContextProvider";
import { StringObjectType } from "../StringObjectType";

/**
 * Step properties injected by the WizardStep component.
 *
 * @export
 * @interface CustomStepInjectedProps
 * @template TStep
 */
export interface CustomStepInjectedProps<TStep> {
  stepResult: TStep;
  setStepResult: (stepResult: TStep) => void;
  stepId: string;
  stepTitle: string;
  stepDescription?: string;
  goNext: () => void;
  goPrevious: () => void;
}

/**
 * Component properties of the WizardStep component.
 *
 * @export
 * @interface WizardStepProps
 * @template TResult
 * @template TStepId
 */
export interface WizardStepProps<
  TResult extends StringObjectType<TResult>,
  TStepId extends Extract<keyof TResult, string>
> {
  stepId: TStepId;
  stepTitle: string;
  stepDescription?: string;
  component: React.FC<CustomStepInjectedProps<TResult[TStepId]>>;
  preventUnmount?: boolean;
}

/**
 * Helper component that creates steps in the Wizard component.
 *
 * @template TResult
 * @template TStepId
 * @param {WizardStepProps<TResult, TStepId>} {
 *   stepId,
 *   stepTitle,
 *   stepDescription,
 *   component,
 * }
 * @returns {JSX.Element}
 */
function WizardStep<
  TResult extends StringObjectType<TResult>,
  TStepId extends Extract<keyof TResult, string>
>({
  stepId,
  stepTitle,
  stepDescription,
  component,
}: WizardStepProps<TResult, TStepId>): JSX.Element {
  const { setStepNumber } = useStepContext();
  const [stepResult, setStepResult] = useStepResult<TResult, TStepId>(stepId);

  // Prevent reference changes for next/previous functions
  // Helps downstream dependencies avoid unnecessary rerenders.
  const goNext = useCallback(() => setStepNumber((s: number) => s + 1), [
    setStepNumber,
  ]);
  const goPrevious = useCallback(() => setStepNumber((s: number) => s - 1), [
    setStepNumber,
  ]);

  const injectProperties = useMemo(
    () => ({
      stepId,
      stepResult,
      setStepResult,
      stepTitle,
      stepDescription,
      goNext,
      goPrevious,
    }),
    [
      stepId,
      stepTitle,
      stepDescription,
      stepResult,
      setStepResult,
      goNext,
      goPrevious,
    ]
  );

  // Memoized component + props prevents re-render unless step result has changed.
  // Used to optimize "prevent unmount" feature of Wizard.
  return useMemo(() => React.createElement(component, injectProperties), [
    component,
    injectProperties,
  ]);
}

export default WizardStep;
